/**
 * Kleines ZIP-Paket für den vollständigen Export (ohne Kompression, „stored“): Textdateien und Belege in einer Datei,
 * die jedes Betriebssystem öffnet. Keine Abhängigkeit, läuft im Browser und in Node (Tests).
 */
import { t } from "../../../lib/sprache";

export interface ZipEntry {
  /** Pfad im Paket, z. B. „tabellen/talents.csv“ */
  name: string;
  data: Uint8Array;
  /** Änderungszeitpunkt (Standard: jetzt) */
  date?: Date;
}

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crcUpdate(c: number, data: Uint8Array): number {
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  return c;
}
export function crc32(data: Uint8Array): number {
  return (crcUpdate(0xffffffff, data) ^ 0xffffffff) >>> 0;
}
/** Prüfsumme einer großen Datei in Stücken (Videos der Content-Ablage): der Browser hält nie die ganze Datei als Puffer. */
export async function crc32Blob(blob: Blob, chunk = 8 * 1024 * 1024): Promise<number> {
  let c = 0xffffffff;
  for (let pos = 0; pos < blob.size; pos += chunk) c = crcUpdate(c, new Uint8Array(await blob.slice(pos, pos + chunk).arrayBuffer()));
  return (c ^ 0xffffffff) >>> 0;
}

/** Zeitpunkt im DOS-Format (lokale Zeit, 2-Sekunden-Schritte). */
function dosTime(d: Date): { time: number; date: number } {
  const year = Math.max(1980, d.getFullYear());
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

const utf8 = new TextEncoder();
export function textEntry(name: string, text: string, date?: Date): ZipEntry {
  return { name, data: utf8.encode(text), ...(date ? { date } : {}) };
}

/** Pfade eindeutig machen: gleiche Namen bekommen „-2“, „-3“ … vor der Endung. */
export function uniqueNames(names: readonly string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((n) => {
    const count = seen.get(n) ?? 0;
    seen.set(n, count + 1);
    if (!count) return n;
    const dot = n.lastIndexOf(".");
    return dot > n.lastIndexOf("/") ? `${n.slice(0, dot)}-${count + 1}${n.slice(dot)}` : `${n}-${count + 1}`;
  });
}

/** Große Datei als Blob mit vorab berechneter Prüfsumme (crc32Blob): bleibt beim Packen ein Blob (der Browser lagert ihn aus). */
export interface ZipBlobEntry {
  name: string;
  blob: Blob;
  crc: number;
  date?: Date;
}
const isBlobEntry = (e: ZipEntry | ZipBlobEntry): e is ZipBlobEntry => "blob" in e;

/** Grenzen des einfachen ZIP-Formats (ohne ZIP64): 65.535 Einträge, 4 GB */
export const ZIP_MAX_ENTRIES = 0xffff;
export const ZIP_MAX_BYTES = 0xffffffff;

/** Teile des ZIP (ohne Kompression, UTF-8-Dateinamen, Bit 11) in Reihenfolge; Blobs bleiben Blobs. */
export function buildZipParts(entries: readonly (ZipEntry | ZipBlobEntry)[]): (Uint8Array | Blob)[] {
  if (entries.length > ZIP_MAX_ENTRIES) throw new Error(t(`Das Paket darf höchstens ${ZIP_MAX_ENTRIES} Dateien enthalten.`, `The package may contain at most ${ZIP_MAX_ENTRIES} files.`));
  const names = uniqueNames(entries.map((e) => e.name.replace(/^\/+/, "")));
  const locals: (Uint8Array | Blob)[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  entries.forEach((e, i) => {
    const name = utf8.encode(names[i]!);
    const size = isBlobEntry(e) ? e.blob.size : e.data.length;
    const crc = isBlobEntry(e) ? e.crc : crc32(e.data);
    const { time, date } = dosTime(e.date ?? new Date());
    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, 0x0800, true);
    lv.setUint16(8, 0, true);
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true);
    lv.setUint32(22, size, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true);
    local.set(name, 30);
    locals.push(local, isBlobEntry(e) ? e.blob : e.data);

    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    central.set(name, 46);
    centrals.push(central);
    offset += local.length + size;
    if (offset > ZIP_MAX_BYTES) throw new Error(t("Das Paket wäre größer als 4 GB. Bitte ohne Videos exportieren (Download-Links).", "The package would be larger than 4 GB. Please export without videos (download links)."));
  });
  const centralSize = centrals.reduce((a, c) => a + c.length, 0);
  if (offset + centralSize + 22 > ZIP_MAX_BYTES) throw new Error(t("Das Paket wäre größer als 4 GB. Bitte ohne Videos exportieren (Download-Links).", "The package would be larger than 4 GB. Please export without videos (download links)."));
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);
  return [...locals, ...centrals, end];
}

/** ZIP als Blob für den Download (große Dateien werden nicht in einen Puffer kopiert). */
export function buildZipBlob(entries: readonly (ZipEntry | ZipBlobEntry)[]): Blob {
  return new Blob(buildZipParts(entries) as BlobPart[], { type: "application/zip" });
}

/** ZIP ohne Kompression mit UTF-8-Dateinamen (Bit 11) als ein Puffer (nur Einträge mit Bytes). */
export function buildZip(entries: readonly ZipEntry[]): Uint8Array {
  const parts = buildZipParts(entries) as Uint8Array[];
  const out = new Uint8Array(parts.reduce((a, p) => a + p.length, 0));
  let pos = 0;
  for (const part of parts) {
    out.set(part, pos);
    pos += part.length;
  }
  return out;
}
